import Title from '../../common/Title/Title';
import {
  useForm,
  Controller,
  Control,
  useFieldArray,
  FieldArrayWithId,
  UseFieldArrayRemove,
} from 'react-hook-form';
import Section from '../../common/Section/Section';
import { Input } from '../../common/Input/Input';
import Button from '../../common/Button/Button';
import SelectComponent from '../../common/Select/Select';
import { CreateRoleRequest, RoleFeatureInput } from '../../../redux/services/types/Role/roleRecord';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './PermissionRoleDetail.module.scss';
import { useEffect, useState } from 'react';
import { useGetFeaturesQuery } from '../../../redux/services/featuresAPI';
import { ColumnDef } from '../../common/Table/useTable';
import Table, { TableActions } from '../../common/Table/Table';
import IconButton from '../../common/IconButton/IconButton';
import { FeatureRecord, RoleRecord } from '../../../redux/services/types/types';
import { CancelButton } from '../../common/FormUtils/FormUtils';
import {
  useCreateRoleMutation,
  useGetRoleDetailsByIdQuery,
  useGetRolesQuery,
  useUpdateRoleMutation,
} from '../../../redux/services/rolesAPI';
import { useToast } from '../../common/Toast/ToastContext';
import Alert from '../../common/Alert/Alert';

type RoleDetailForm = Omit<CreateRoleRequest, 'permissionRoleId'>;

const initialFormState: RoleDetailForm = {
  name: '',
  permissionRoleFeatures: [],
};

export const PermissionRoleDetail = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [featureList, setFeatureList] = useState([] as Array<any>);
  const [featureValue, setFeatureValue] = useState(0 as number);

  const { featureData } = GetFeatureList();

  const [params] = useSearchParams();
  const permissionRoleId = params.get('id');

  const [createRole, { isLoading: isCreating }] = useCreateRoleMutation();
  const [updateRole, { isLoading: isUpdating }] = useUpdateRoleMutation();

  const { register, handleSubmit, control, formState, reset } = useForm<RoleDetailForm>({
    defaultValues: initialFormState,
  });

  const {
    fields: roleFeaturesFields,
    append: appendRoleFeatureField,
    remove: removeRoleFeatureField,
  } = useFieldArray({
    control,
    name: 'permissionRoleFeatures',
  });

  const { refetch: refetchRoles } = useGetRolesQuery();

  const {
    data: roleDetailData,
    isLoading,
    refetch: refetchRole,
    error,
    isError,
  } = useGetRoleDetailsByIdQuery(permissionRoleId ?? '', {
    skip: !permissionRoleId,
  });

  useEffect(() => {
    if (isError) {
      addToast({
        content: (error as Error).message,
        type: 'error',
      });

      refetchRoles();

      navigate('/permission/role/list');
    }
  }, [isError, error, addToast, navigate, refetchRoles]);

  useEffect(() => {
    if (featureData && featureData.length > 0) {
      const featureDropdownListData = featureData
        .filter((data) => data.status === 1)
        .map((feature) => {
          return {
            label: `${feature.name} - ${feature.applicationName}`,
            value: feature.permissionFeatureId,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

      const initList = [{ label: 'Select...', value: 0, isDisabled: true }];

      const featureDropdownList = [...initList, ...featureDropdownListData];

      setFeatureList(featureDropdownList);
    }
  }, [featureData]);

  useEffect(() => {
    if (roleDetailData && featureData) {
      const editRoleInput = reMapRoleDataModel(roleDetailData, featureData);
      reset(editRoleInput);
    }
  }, [featureData, roleDetailData, reset]);

  useEffect(() => {
    if (featureData && featureData.length > 0) {
      const featureDropdownListData = featureData
        .filter(
          (data) =>
            data.status === 1 &&
            roleFeaturesFields.findIndex(
              (x) => x.permissionFeatureId === data.permissionFeatureId,
            ) === -1,
        )
        .map((feature) => {
          return {
            label: `${feature.name} - ${feature.applicationName}`,
            value: feature.permissionFeatureId,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

      const initList = [{ label: 'Select...', value: 0, isDisabled: true }];

      const featureDropdownList = [...initList, ...featureDropdownListData];

      setFeatureList(featureDropdownList);
    }
  }, [featureData, roleFeaturesFields]);

  const onSubmit = async (data: RoleDetailForm) => {
    const isNewRole = !roleDetailData;

    try {
      isNewRole ? await createRole({ ...data }).unwrap() : await updateRole({ ...data }).unwrap();

      addToast({
        content: `Role ${isNewRole ? 'added' : 'updated'} successfully !`,
        type: 'success',
      });

      if (!isNewRole) refetchRole();

      refetchRoles();
      navigate('/permission/role/list');
    } catch (err) {
      addToast({
        content: (
          <Alert
            title={`Error ${isNewRole ? 'creating' : 'updating'} role`}
            message={(err as Error).message}
            type="error"
          />
        ),
        type: 'error',
      });
    }
  };

  const onFeatureValueChange = (val: number) => {
    if (val) {
      setFeatureValue(val);
    }
  };

  const onAddFeatureButtonClick = () => {
    const featureInputData = (featureData as FeatureRecord[]).find(
      (x) => x.permissionFeatureId === featureValue,
    ) as FeatureRecord;

    const roleFeatureData = roleDetailData?.permissionRoleFeatures
      ? roleDetailData.permissionRoleFeatures.find((x) => x.permissionFeatureId === featureValue)
      : null;

    const featureInput = {
      permissionFeatureId: featureInputData.permissionFeatureId,
      permissionFeatureName: featureInputData.name,
      applicationName: featureInputData.applicationName,
      insert: roleFeatureData ? roleFeatureData.insert : 0,
      select: roleFeatureData ? roleFeatureData.select : 0,
      update: roleFeatureData ? roleFeatureData.update : 0,
      delete: roleFeatureData ? roleFeatureData.delete : 0,
      status: 1,
    };

    appendRoleFeatureField(featureInput);

    setFeatureValue(0);
  };

  const renderForm = () => {
    return (
      <form onSubmit={handleSubmit(onSubmit, console.error)} data-testid="role-detail-form">
        <div className={`${styles['input-row']} row`}>
          <div className="column-three">
            <Input
              id="roleName"
              label={'Role Name *'}
              readOnly={false}
              error={formState.errors.name?.message}
              {...register('name', {
                required: { value: true, message: 'Role name is required !' },
              })}
            />
          </div>
        </div>
        <div className="h-3"></div>
        <Section text={'Map Feature to Role'}>
          <div className="h-1"></div>
          <div className={`${styles['input-row']} row`}>
            <div className="column-two">
              <SelectComponent
                isMulti={false}
                label={'Feature Name'}
                value={featureList.find((opt) => opt.value === featureValue)}
                options={featureList}
                isSearchable
                onChange={(val) => onFeatureValueChange(val ? val.value : null)}
                inputId="feature-name-list"
                data-testid={'feature-name-list'}
              />
            </div>
            <div className={`column-two ${styles['add-feature']}`}>
              <Button
                text={'Add Feature Role'}
                color="primary-black"
                disabled={!featureValue}
                onClick={onAddFeatureButtonClick}
                data-testid="add-feature-click"
              />
            </div>
          </div>
          <div className="h-3"></div>
          <FeatureRoleTable
            control={control}
            fields={roleFeaturesFields}
            remove={removeRoleFeatureField}
          />
        </Section>
        <div className="h-4" />
        <div className={styles['actions']}>
          <CancelButton showAlert={formState.isDirty} onCancel={() => navigate(-1)} />
          <Button
            text={isCreating || isUpdating ? 'Loading ...' : 'Save'}
            color="primary-black"
            disabled={!formState.isDirty}
          />
        </div>
      </form>
    );
  };

  const getTitleText = () => {
    return permissionRoleId ? 'Edit Role' : 'Add Role';
  };

  return (
    <div className="container">
      <Title text={getTitleText()} />
      <div className="h-3" />
      {isLoading && !roleDetailData ? 'Loading Role Detail...' : renderForm()}
    </div>
  );
};

const reMapRoleDataModel = (
  roleData: RoleRecord,
  featureRecords: FeatureRecord[],
): CreateRoleRequest => {
  let result: CreateRoleRequest = { ...roleData };

  if (featureRecords && featureRecords.length > 0 && result.permissionRoleFeatures?.length > 0) {
    result.permissionRoleFeatures = result.permissionRoleFeatures.map((feature) => {
      const featureItem = featureRecords.find(
        (x) => x.permissionFeatureId === feature.permissionFeatureId,
      ) as FeatureRecord;
      const permissionFeatureName = featureItem ? featureItem.name : '';
      const applicationName = featureItem ? featureItem.applicationName : '';

      return { ...feature, permissionFeatureName, applicationName } as RoleFeatureInput;
    });
  } else {
    result['permissionRoleFeatures'] = [];
  }

  return result;
};

const FeatureRoleTable = ({
  control,
  fields,
  remove,
}: {
  control: Control<RoleDetailForm>;
  fields: FieldArrayWithId<RoleDetailForm, 'permissionRoleFeatures', 'id'>[];
  remove: UseFieldArrayRemove;
}) => {
  const removeFeatureRole = ({ record }: { record: RoleFeatureInput }) => {
    const fieldValueIndex = fields.findIndex(
      (x) => x.permissionFeatureId === record.permissionFeatureId,
    );

    if (fieldValueIndex >= 0) remove(fieldValueIndex);
  };

  const colDefs: Array<ColumnDef<RoleFeatureInput>> = [
    { field: 'permissionFeatureName', header: 'Feature Name' },
    { field: 'applicationName', header: 'Application Name' },
    {
      field: 'insert',
      header: 'Create',
      renderCell: (record, colDef, rowIndex) => (
        <Controller
          control={control}
          name={`permissionRoleFeatures.${rowIndex}.insert`}
          render={({ field }) => (
            <input
              type="checkbox"
              className={styles['custom-checkbox']}
              checked={field.value === 1}
              onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
              data-testid={`checkbox-${rowIndex}-insert`}
            />
          )}
        />
      ),
    },
    {
      field: 'select',
      header: 'Read',
      renderCell: (record, colDef, rowIndex) => (
        <Controller
          control={control}
          name={`permissionRoleFeatures.${rowIndex}.select`}
          render={({ field }) => (
            <input
              type="checkbox"
              className={styles['custom-checkbox']}
              checked={field.value === 1}
              onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
              data-testid={`checkbox-${rowIndex}-select`}
            />
          )}
        />
      ),
    },
    {
      field: 'update',
      header: 'Update',
      renderCell: (record, colDef, rowIndex) => (
        <Controller
          control={control}
          name={`permissionRoleFeatures.${rowIndex}.update`}
          render={({ field }) => (
            <input
              type="checkbox"
              className={styles['custom-checkbox']}
              checked={field.value === 1}
              onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
              data-testid={`checkbox-${rowIndex}-update`}
            />
          )}
        />
      ),
    },
    {
      field: 'delete',
      header: 'Delete',
      renderCell: (record, colDef, rowIndex) => (
        <Controller
          control={control}
          name={`permissionRoleFeatures.${rowIndex}.delete`}
          render={({ field }) => (
            <input
              type="checkbox"
              className={styles['custom-checkbox']}
              checked={field.value === 1}
              onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
              data-testid={`checkbox-${rowIndex}-delete`}
            />
          )}
        />
      ),
    },
    {
      header: 'Actions',
      renderCell: (record, colDef) => (
        <TableActions>
          <IconButton
            icon="delete"
            action={() => removeFeatureRole({ record })}
            data-testid="delete-icon"
          />
        </TableActions>
      ),
    },
  ];

  return (
    <>
      <Table
        colDefs={colDefs}
        data={fields}
        isLoading={false}
        getRowId={(role) => role.permissionFeatureId.toString()}
        sortable={false}
      />
    </>
  );
};

const GetFeatureList = () => {
  const { data, isLoading } = useGetFeaturesQuery();

  return { featureData: data, isFeatureDataLoading: isLoading };
};

export default PermissionRoleDetail;
